import { useQuery } from '@tanstack/react-query';

import { useRequest } from '@frontend/api';
import type { QueryFunction } from '@frontend/api/src/common/queryFunction';

import type {
  PartThumbnailImageData,
  PartThumbnailImageQueryKey,
  UsePartThumbnailImageParams,
} from './types';
import { getPartThumbnailImageQueryKey } from './util';

export const usePartThumbnailImage: QueryFunction<
  PartThumbnailImageData,
  [UsePartThumbnailImageParams],
  PartThumbnailImageQueryKey
> = (params, queryFunctionOptions = {}) => {
  const { partFileUuid } = params;
  const { requestOptions } = queryFunctionOptions;
  const queryOptions = queryFunctionOptions.queryOptions ?? {};
  queryOptions.enabled = !!partFileUuid && queryOptions?.enabled !== false;

  const request = useRequest(requestOptions);

  const queryKey = getPartThumbnailImageQueryKey(params);

  return useQuery<
    PartThumbnailImageData,
    unknown,
    PartThumbnailImageData,
    PartThumbnailImageQueryKey
  >({
    ...queryOptions,
    staleTime: Infinity,
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await request(
        `/parts/files/${params.partFileUuid}/thumbnail/`,
        {
          init: {
            method: 'GET',
            signal,
          },
          retryOptions: {
            retryDelay: 1000,
            getShouldRetry: ({ response }) => response.status === 202, // 202 indicates that the thumbnail is still being generated
          },
        },
      );

      const thumbnailBlob = response instanceof Blob ? response : null;

      return { thumbnailBlob };
    },
  });
};
